import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import env from 'Env';
import { ageRatingBoard, currencyCode } from 'Helpers/Geo/data';
import ProductCarousel from 'Components/Product/Carousel/ProductCarousel';
import Drm from 'Components/Utility/Drm';
import FixedImg from 'Components/Utility/FixedImg';
import Price from 'Containers/Utility/Price/Price';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import StarRating from 'Components/ProductReviews/UserReviews/StarRating';
import SteamDeckInformation from 'Components/Utility/SteamDeck/SteamDeckInformation';
import useViewport from 'Hooks/useViewport';
import ProductCover from 'Components/Utility/ProductCover';

import 'Components/Order/MysteryProductDetails/MysteryProductDetails.scss';

const MysteryProductDetails = ({ siteTheme, product, cover, mobileLayout }) => {
  const scoreThreshold = env.percentRecommendedThreshold;
  const showAgeRatingWarning = get(product, `age.${ageRatingBoard}`) === 18;
  const currentDrm = ProductRequirements.CurrentDrm(product);
  const validPlatforms = ProductRequirements.validPlatforms(product);
  const userReviewRatingScore = get(product.userReviewSummary, 'rating_score');
  const userReviewTotalRatings = get(product.userReviewSummary, 'total_ratings');
  const metacriticScore = get(product, 'metaCritic.score', null);
  const opencriticScore = get(product, 'openCritic.score', null);
  const showMetacritic = metacriticScore && (metacriticScore >= scoreThreshold);
  const showOpencritic = opencriticScore && (opencriticScore >= scoreThreshold);
  const { isBelowMd, isBelowSm, isAboveSm, isAboveLg } = useViewport();
  const dates = ProductRequirements.ReleaseDates(product);
  const productVideos = ProductRequirements.ProductVideos(product);
  const released = dates.releaseDate;

  const steamId = get(product, 'steam.id');
  const steamType = get(product, 'steam.type', 'app');
  const steamUrl = `http://store.steampowered.com/${steamType}/${steamId}`;

  let tagsToShow = 10;
  if (isBelowMd) tagsToShow = 4;
  if (isBelowSm) tagsToShow = 3;

  const price = get(product, `price.${currencyCode}`, 0) * 100;

  return (
    <section
      className={classNames('MysteryProductDetails', { 'MysteryProductDetails--mobile': mobileLayout })}
      id="mystery-product-details"
    >
      <div className="MysteryProductDetails__info">
        <div className="MysteryProductDetails__titleAgeRatingContainer">
          <div className="MysteryProductDetails__title h2">{product.name}</div>
          {showAgeRatingWarning &&
            <div className="MysteryProductDetails__ageRating">
              <FixedImg
                src={`${ageRatingBoard}/18.png`}
                subfolder="age-ratings"
                alt="Adult content"
                width="30"
              />
            </div>
          }
        </div>

        <div className="MysteryProductDetails__topContainer">
          {currentDrm &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__topContainer__drmContainer">
                {!mobileLayout &&
                  <Drm drmType={currentDrm} />
                }
                {mobileLayout &&
                  <Drm drmType={currentDrm} colorOverride="dark" />
                }
              </div>
            </React.Fragment>
          }

          {/* Platforms */}
          {!isEmpty(validPlatforms) &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__topContainer__platformsContainer">
                {validPlatforms && validPlatforms.includes('windows') &&
                  <FontAwesomeIcon icon={fabFaWindows} size="lg" />
                }
                {validPlatforms && validPlatforms.includes('mac') &&
                  <FontAwesomeIcon icon={fabFaApple} size="lg" />
                }
                {validPlatforms && validPlatforms.includes('linux') &&
                  <FontAwesomeIcon icon={fabFaLinux} size="lg" />
                }
              </div>
            </React.Fragment>
          }

          {/* User Review Ratings */}
          {userReviewRatingScore > 3 &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__topContainer__userRatingContainer">
                <StarRating rating={userReviewRatingScore} />
                {isAboveLg ?
                  <div className="MysteryProductDetails__text">
                    <FormattedMessage
                      id="MysteryProductDetails.UserRatings"
                      defaultMessage="{userReviewTotalRatings} User Ratings"
                      values={{
                        userReviewTotalRatings,
                      }}
                    />
                  </div>
                  :
                  <div className="MysteryProductDetails__text">
                    ({userReviewTotalRatings})
                  </div>
                }
              </div>

            </React.Fragment>
          }

          {/* Critic Review Ratings */}
          {(showMetacritic || showOpencritic) &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__topContainer_criticContainer">
                {showMetacritic &&
                  <div className="MysteryProductDetails__topContainer__criticSection">
                    <div className="MysteryProductDetails__topContainer__criticScore">
                      {metacriticScore}
                    </div>
                    <div className="ReviewSection__criticsLogo ReviewSection__criticsLogo--metacritic">
                      {!mobileLayout &&
                        <img src={`https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-full-${siteTheme === 'light' ? 'black' : 'white'}.svg`} alt="" />
                      }
                      {mobileLayout &&
                        <img src="https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-full-black.svg" alt="" />
                      }
                    </div>
                  </div>
                }

                {!showMetacritic && showOpencritic &&
                  <div className="MysteryProductDetails__topContainer__criticSection">
                    <div className="MysteryProductDetails__topContainer__criticScore">
                      {opencriticScore}
                    </div>
                    <div className="ReviewSection__criticsLogo ReviewSection__criticsLogo--opencritic">
                      {!mobileLayout &&
                        <img src={`https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-full-${siteTheme === 'light' ? 'black' : 'white'}.svg`} alt="" />
                      }
                      {mobileLayout &&
                        <img src="https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-full-black.svg" alt="" />
                      }
                    </div>
                  </div>
                }
              </div>
            </React.Fragment>
          }
          {/* steam  */}
          {product.steam && (product.steam.deck_support === 'verified' || product.steam.deck_support === 'playable') && isAboveSm &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__topContainer__steamDeckSection">
                <SteamDeckInformation
                  longTitle
                  type={product.steam.deck_support}
                  details={product.steam.deck_details}
                  product={name}
                />
              </div>
            </React.Fragment>
          }
          {/* Mobile age  */}
          {showAgeRatingWarning && isBelowMd &&
            <React.Fragment>
              <div className="MysteryProductDetails__topContainer__divider">
                |
              </div>
              <div className="MysteryProductDetails__ageRating">
                <FixedImg
                  src={`${ageRatingBoard}/18.png`}
                  subfolder="age-ratings"
                  alt="Adult content"
                  width="30"
                />
              </div>
            </React.Fragment>
          }
        </div>

        {!isEmpty(product.categories) &&
          <div className="MysteryProductDetails__tagsContainer">
            {product.categories.slice(0, tagsToShow).map(tag =>
              <React.Fragment>
                {typeof tag === 'string' ?
                  <div key={tag} className="MysteryProductDetails__tag">{tag}</div>
                  :
                  <React.Fragment>
                    {tag.link ?
                      <Link key={tag.slug} to={`/categories/${tag.slug}`} className="MysteryProductDetails__tag MysteryProductDetails__tag--clickable">
                        {tag.name}
                      </Link>
                      :
                      <div key={tag.slug} className="MysteryProductDetails__tag">
                        {tag.name}
                      </div>
                    }
                  </React.Fragment>
                }
              </React.Fragment>,
            )}
          </div>
        }

        {false && // after commercial marketing meeting removed, may return in follow up test
          <div className="MysteryProductDetails__productInfoContainer">
            <div className="MysteryProductDetails__text MysteryProductDetails__productInfoSection">
              <FormattedMessage
                id="MysteryProductDetails.releaseDate"
                defaultMessage="Release Date: {releaseDate}"
                values={{ releaseDate: <FormattedDate value={released} year="numeric" month="long" day="2-digit" /> }}
              />
            </div>
            <div className="MysteryProductDetails__text MysteryProductDetails__productInfoSection">
              <FormattedMessage
                id="MysteryProductDetails.value"
                defaultMessage="Value: {price}"
                values={{
                  price: <Price price={{ [currencyCode]: price }} />,
                }}
              />
            </div>
            <a
              href={steamUrl}
              className="MysteryProductDetails__link"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <FormattedMessage
                id="MysteryProductDetails.viewProductOnSteam"
                defaultMessage="View on Steam"
              />
              <span>
                <FontAwesomeIcon icon={faUpRightFromSquare} className="ml-1" />
              </span>
            </a>
          </div>
        }
      </div>

      {isEmpty(productVideos) &&
        <div className="MysteryProductDetails__videoContainer">
          <ProductCover
            cover={cover}
            alt={`Product cover for ${cover}`}
            sizesOverride="(min-width: 1520px) 872px, (min-width: 1200px) 740px, (min-width: 992px) 620px, 95vw"
          />
        </div>
      }

      {!isEmpty(productVideos) &&
        <div className="MysteryProductDetails__videoContainer">
          <ProductCarousel
            productCoverOverrride
            videos={[productVideos[0]]}
            ratings={product.age}
            bundleCover={product.bundleCover}
            type={product.type}
            sizesOverride="(min-width: 1520px) 872px, (min-width: 1200px) 740px, (min-width: 992px) 620px, 95vw"
          />
        </div>
      }

    </section>
  );
};

const mapStateToProps = ({
  siteTheme,
}) => ({
  siteTheme,
});

MysteryProductDetails.propTypes = {
  cover: PropTypes.string,
  mobileLayout: PropTypes.bool,
  product: PropTypes.shape({}),
  siteTheme: PropTypes.string,
};

MysteryProductDetails.defaultProps = {
  cover: '',
  mobileLayout: false,
  product: {},
  siteTheme: 'dark',
};


export default connect(mapStateToProps)(MysteryProductDetails);
